<template>
  <div class="login-container">
    <div class="login-card">
      <div class="brand-section">
        <i class="fas fa-toolbox text-5xl mb-4 text-primary"></i>
        <h2 v-if="loginStep === 'credentials'">{{ isLoginMode ? 'Connexion Toolbox' : 'Inscription' }}</h2>
        <h2 v-else>Authentification</h2>
      </div>
      
      <!-- Step 1: Credentials -->
      <form v-if="loginStep === 'credentials'" @submit.prevent="handleLogin" class="auth-form">
        <div class="input-group">
          <label>Email</label>
          <input type="email" v-model="email" required placeholder="nom@exemple.com" />
        </div>
        
        <div class="input-group">
          <label>Mot de passe</label>
          <input type="password" v-model="password" required minlength="6" placeholder="••••••••" />
        </div>
        
        <button type="submit" :disabled="loading" class="btn-primary">
          {{ isLoginMode ? 'Se connecter' : 'Créer un compte' }}
        </button>
      </form>

      <!-- Step 2: 2FA Verification -->
      <form v-else @submit.prevent="handleVerify2fa" class="auth-form">
        <p class="text-sm">Entrez le code à 6 chiffres de votre application d'authentification.</p>
        <div class="input-group">
          <label>Code de sécurité</label>
          <input type="text" v-model="otpCode" required maxlength="6" pattern="\d{6}" placeholder="000000" class="otp-input" />
        </div>
        
        <button type="submit" :disabled="loading" class="btn-primary mt-4">
          Valider le code
        </button>
        <button type="button" class="btn-link" @click="loginStep = 'credentials'">Retour</button>
      </form>
      
      <p v-if="error" class="error-msg">{{ error }}</p>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'

const email = ref('')
const password = ref('')
const otpCode = ref('')
const loading = ref(false)
const error = ref('')
const isLoginMode = ref(true)
const loginStep = ref('credentials')
const router = useRouter()

async function handleLogin() {
  loading.value = true
  error.value = ''
  
  const endpoint = isLoginMode.value ? '/api/login' : '/api/register'
  
  try {
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.value, password: password.value })
    })

    const data = await res.json().catch(() => ({}))
    
    if (res.ok) {
      if (data.status === '2fa_required') {
        loginStep.value = '2fa'
      } else {
        completeLogin(data.token, data.roles || [])
      }
    } else {
      if (res.status === 409) {
          error.value = "Cet email est déjà utilisé."
      } else if (res.status === 400 && data.error) {
          error.value = data.error === 'Invalid email' ? "Email invalide." : "Le mot de passe doit faire au moins 6 caractères."
      } else {
          error.value = isLoginMode.value ? "Identifiants invalides." : "Erreur lors de l'inscription."
      }
    }
  } catch (err) {
    error.value = "Erreur de connexion au serveur."
  }
  loading.value = false
}

async function handleVerify2fa() {
    loading.value = true
    error.value = ''
    try {
        const response = await fetch('/api/login/2fa', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                email: email.value,
                password: password.value,
                code: otpCode.value
            })
        })
        const data = await response.json()
        if (response.ok) {
            completeLogin(data.token, data.roles || [])
        } else {
            error.value = data.error || 'Code invalide'
        }
    } catch (e) {
        error.value = 'Erreur lors de la vérification'
    } finally {
        loading.value = false
    }
}

function completeLogin(token, roles) {
    localStorage.setItem('apiToken', token)
    localStorage.setItem('userRoles', JSON.stringify(roles))
    router.push({ name: 'suite_dashboard' })
}
</script>

<style scoped>
.login-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    background: #0f172a;
    font-family: 'Inter', sans-serif;
    color: #f8fafc;
}

.login-card {
    background: #1e293b;
    padding: 40px;
    border-radius: 16px;
    box-shadow: 0 10px 25px rgba(0,0,0,0.5);
    width: 100%;
    max-width: 420px;
    border: 1px solid rgba(255,255,255,0.05);
}

.brand-section {
    text-align: center;
    margin-bottom: 30px;
}

.brand-logo {
    height: 64px;
    width: 64px;
    margin-bottom: 16px;
    filter: drop-shadow(0 0 8px rgba(59, 130, 246, 0.3));
}

.brand-section h2 {
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0;
    color: #e2e8f0;
}

.auth-form {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.input-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

label {
    font-size: 0.85rem;
    font-weight: 600;
    color: #94a3b8;
}

input {
    padding: 12px 14px;
    background: #0f172a;
    border: 1px solid #334155;
    border-radius: 8px;
    color: #f8fafc;
    font-size: 1rem;
    transition: all 0.2s;
}

input:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.otp-input {
    text-align: center;
    letter-spacing: 0.5rem;
    font-size: 1.5rem;
    font-weight: bold;
}

.btn-primary {
    background: #3b82f6;
    color: white;
    border: none;
    padding: 14px;
    border-radius: 8px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s;
    font-size: 1rem;
}

.btn-primary:hover:not(:disabled) {
    background: #2563eb;
    transform: translateY(-1px);
}

.btn-primary:active {
    transform: translateY(0);
}

.btn-primary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.error-msg {
    color: #ef4444;
    font-size: 0.85rem;
    margin-top: 20px;
    text-align: center;
    background: rgba(239, 68, 68, 0.1);
    padding: 10px;
    border-radius: 6px;
    border: 1px solid rgba(239, 68, 68, 0.2);
}

.text-sm {
    font-size: 0.9rem;
    color: #94a3b8;
    text-align: center;
    line-height: 1.5;
}

.btn-link {
    background: none;
    border: none;
    color: #3b82f6;
    cursor: pointer;
    text-decoration: underline;
    font-size: 0.85rem;
    text-align: center;
    margin-top: 10px;
}

.mt-4 { margin-top: 20px; }
</style>
